import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-neutral-950">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/c6311804-acd2-45f2-a222-5c01e8777e12/images/eafe7916c055ffd56ad5875e7aeb78a1a2638525ffe8c0e3987c0689481f1c2b.png"
          alt="Konattu Group" className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="bg-white rounded-md p-4 inline-flex w-fit mb-6">
            <img src="/assets/konattu-logo.png" alt="Konattu Group HRMS" className="h-20 w-auto object-contain" />
          </div>
          <p className="text-neutral-300 text-sm max-w-md leading-relaxed">
            Centralized staff management, attendance tracking, and performance monitoring across all 15 branches.
          </p>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-neutral-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center mb-8 lg:hidden">
            <img src="/assets/konattu-logo.png" alt="Konattu HRMS" className="h-12 w-auto object-contain" />
          </div>

          <h1 className="font-heading font-black text-neutral-950 text-3xl sm:text-4xl tracking-tight" data-testid="login-title">
            Sign In
          </h1>
          <p className="text-neutral-500 text-sm mt-2 mb-8">Enter your credentials to access the system</p>

          <Card className="border border-neutral-200 shadow-sm rounded-md">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-neutral-700">Email</Label>
                  <Input id="email" type="email" placeholder="name@konattu.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}
                    className="h-11 rounded-sm border-neutral-200 focus:ring-red-600/30"
                    data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-neutral-700">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPw ? 'text' : 'password'} placeholder="Enter password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required
                      autoComplete="current-password"
                      className="h-11 rounded-sm border-neutral-200 pr-12 focus:ring-red-600/30"
                      data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      data-testid="login-password-visibility" aria-label={showPw ? 'Hide password' : 'Show password'} aria-pressed={showPw}
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-neutral-400 hover:text-neutral-600">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-red-600 hover:bg-red-700 text-white rounded-sm font-semibold transition-colors duration-200"
                  data-testid="login-submit-button">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2"><LogIn className="h-4 w-4" /> Sign In</span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-neutral-400 text-xs text-center mt-6">
            Konattu Group HRMS &middot; Centralized HR Management
          </p>
        </div>
      </div>
    </div>
  );
}
